import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from './api';
import { useAuth } from './AuthContext';
import { losApi, PROGRAM_LABELS } from './losApi';
import DealMessagesPanel from './DealMessagesPanel';
import AudreySummaryButton from './AudreySummaryButton';
import AskAudreyButton from './AskAudreyButton';
import PickSelect from './shared/PickSelect';
import { addressLine } from './shared/address';
import { labelFor } from './shared/labels';
import { STAGE_LABELS, stageLabel, roleLabel, humanize, docStatusLabel, docFolderLabel } from './dealLabels';
import { errorMessage } from './errors';
import {
  Handshake, ArrowLeft, ExternalLink, Loader2, Search, X,
  FileSignature, Users, Copy, RotateCw, Calculator, FileText, AlertTriangle, Mail,
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { copyText } from './clipboard';
import { useBorrowerInvite } from './loan-file/shared/useBorrowerInvite';
import { formatDate, formatDateTime } from './kit/time';
import { Input } from '../components/ui/input';

const fmt$ = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const SIDE_OPTIONS = [
  { value: '', label: 'All sides' },
  { value: 're', label: 'Real estate only' },
  { value: 'loan', label: 'Loan only' },
  { value: 'both', label: 'Real estate + loan' },
];
const STATUS_OPTIONS = [
  { value: '', label: 'All stages and statuses' },
  ...Object.entries(STAGE_LABELS).map(([value, label]) => ({ value, label })),
];
const SORT_OPTIONS = [
  { value: 'activity', label: 'Newest activity' },
  { value: 'close_date', label: 'Closing soonest' },
  { value: 'amount', label: 'Largest amount' },
];

export function DealsListPage() {
  const [items, setItems] = useState(null);   // null until the first load settles
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qInput, setQInput] = useState('');
  const [query, setQuery] = useState('');
  const [side, setSide] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('activity');
  const [page, setPage] = useState(1);
  const reqId = useRef(0);

  // Typing settles for a moment before it becomes a request.
  useEffect(() => {
    if (qInput.trim() === query) return undefined;
    const t = setTimeout(() => { setQuery(qInput.trim()); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qInput, query]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      if (query) params.q = query;
      if (side) params.side = side;
      if (status) params.status = status;
      if (sort !== 'activity') params.sort = sort;
      const { data } = await api.get('/deals/list/all', { params });
      if (id !== reqId.current) return;
      const t = data.total || 0;
      const last = Math.max(1, Math.ceil(t / PAGE_SIZE));
      // Rows vanished since the last look: snap back to the last real page.
      if (t > 0 && page > last) { setPage(last); return; }
      setItems(data.items || []);
      setTotal(t);
    } catch (e) {
      if (id !== reqId.current) return;
      setError(errorMessage(e, 'Failed to load deals'));
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [page, query, side, status, sort]);
  useEffect(() => { load(); }, [load]);

  const pick = (setter) => (v) => { setter(v); setPage(1); };
  const filtersActive = Boolean(query || side || status);
  const resetFilters = () => { setQInput(''); setQuery(''); setSide(''); setStatus(''); setPage(1); };

  if (items === null && !error) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading deals…</div>;

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = rangeStart - 1 + (items ? items.length : 0);
  return (
    <div className="p-4 md:p-8" data-testid="deals-page">
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }}>Deals</h1>
        <p className="text-slate-500 mt-1">Every transaction and loan file you can see — brokerage, lender, or both.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            type="text" value={qInput} onChange={(e) => setQInput(e.target.value)}
            placeholder="Search name, address, loan number or ID" aria-label="Search deals" data-testid="deals-search"
            className="h-10 pl-9 pr-3 bg-white"
          />
        </div>
        <div className="w-44"><PickSelect label="Side" value={side} onChange={pick(setSide)} options={SIDE_OPTIONS} testId="deals-side" /></div>
        <div className="w-52"><PickSelect label="Stage / status" value={status} onChange={pick(setStatus)} options={STATUS_OPTIONS} testId="deals-status" /></div>
        <div className="w-44"><PickSelect label="Sort by" value={sort} onChange={pick(setSort)} options={SORT_OPTIONS} testId="deals-sort" /></div>
        {filtersActive && (
          <button type="button" onClick={resetFilters} className="h-10 px-2 text-xs font-medium underline inline-flex items-center gap-1" style={{ color: '#070B5F' }}>
            <X size={12} /> Reset filters
          </button>
        )}
      </div>

      {error ? (
        <div className="bg-white rounded-2xl p-10 border border-red-100 text-center" data-testid="deals-error">
          <AlertTriangle size={28} className="mx-auto text-red-400 mb-2" />
          <div className="font-semibold text-slate-700">Couldn't load deals</div>
          <div className="text-sm text-slate-500 mt-1">{error}</div>
          <button type="button" onClick={load} className="btn-outline mt-4">Retry</button>
        </div>
      ) : (
        <>
          <div className={`grid gap-3 transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
            {items.map(d => (
              <Link key={d.primary_id} to={`/portal/deals/${d.primary_id}`} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition" data-testid={`deal-${d.primary_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.has_re && <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded" style={{ background: '#F3FAE8', color: '#4c7f21' }}>Real Estate</span>}
                      {d.has_loan && <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-indigo-50 text-indigo-700">Lending</span>}
                      {d.has_re && d.has_loan && <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded" style={{ background: '#070B5F', color: '#A6E56C' }}>UNIFIED</span>}
                      {d.unread > 0 && <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-amber-50 text-amber-700" data-testid={`deal-unread-${d.primary_id}`}>{d.unread} unread</span>}
                    </div>
                    {d.client_name ? (
                      <div className="mt-2 font-serif text-xl font-bold" style={{ color: '#070B5F' }} data-testid={`deal-client-${d.primary_id}`}>{d.client_name}</div>
                    ) : (
                      <div className="mt-2 font-serif text-xl font-bold text-slate-400" data-testid={`deal-client-${d.primary_id}`}>No client name</div>
                    )}
                    {addressLine(d.address) && (
                      <div className="text-sm text-slate-600 mt-0.5" data-testid={`deal-address-${d.primary_id}`}>{addressLine(d.address)}</div>
                    )}
                    <div className="text-sm font-semibold text-slate-700 mt-1">
                      {fmt$(d.contract_price || d.loan_amount)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {d.re_stage && `RE stage: ${stageLabel(d.re_stage)} · `}
                      {d.loan_status && `Loan: ${stageLabel(d.loan_status)} · `}
                      {d.close_date && `Close: ${formatDate(d.close_date)}`}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            {items.length === 0 && (
              <div className="bg-white rounded-2xl p-12 border border-dashed border-slate-200 text-center text-sm text-slate-400">
                {filtersActive ? 'No deals match these filters.' : 'No deals yet.'}
              </div>
            )}
          </div>
          {total > 0 && (
            <div className="mt-4 text-xs text-slate-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span data-testid="deals-range">Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of {total.toLocaleString()}</span>
              {total > PAGE_SIZE && (
                <div className="flex items-center gap-2">
                  <button type="button" className="btn-outline" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Prev</button>
                  <span>Page {page.toLocaleString()} of {lastPage.toLocaleString()}</span>
                  <button type="button" className="btn-outline" disabled={page >= lastPage || loading} onClick={() => setPage(page + 1)}>Next</button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function UnifiedDealPage() {
  const { dealId } = useParams();
  const { user } = useAuth();
  const invite = useBorrowerInvite(dealId);
  const [deal, setDeal] = useState(null);
  const [borrowerLink, setBorrowerLink] = useState(null);
  const [shareOverrides, setShareOverrides] = useState({});
  const [regeneratingKind, setRegeneratingKind] = useState(null);
  const [loadError, setLoadError] = useState(null);   // { status, message }
  const [attempt, setAttempt] = useState(0);          // bumped by Retry
  const [docs, setDocs] = useState(null);             // null = loading
  const [docsError, setDocsError] = useState(null);
  useEffect(() => {
    let live = true;
    setLoadError(null);
    api.get(`/deals/${dealId}`)
      .then(r => { if (live) setDeal(r.data); })
      .catch(e => { if (live) setLoadError({ status: e.response?.status, message: errorMessage(e, 'Failed to load deal') }); });
    losApi.getBorrowerLink(dealId).then(l => { if (live) setBorrowerLink(l); }).catch(() => {});
    return () => { live = false; };
  }, [dealId, attempt]);
  const hasLoanSide = !!deal?.has_loan_side;
  useEffect(() => {
    if (!hasLoanSide) return undefined;
    let live = true;
    setDocs(null);
    setDocsError(null);
    api.get(`/deals/${dealId}/loan-documents`)
      .then(r => { if (live) setDocs(r.data || []); })
      .catch(e => { if (live) setDocsError(errorMessage(e, 'Failed to load documents')); });
    return () => { live = false; };
  }, [dealId, hasLoanSide]);
  if (loadError) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-white rounded-2xl p-10 border border-red-100 text-center max-w-xl mx-auto" data-testid="deal-error">
          <AlertTriangle size={28} className="mx-auto text-red-400 mb-2" />
          <div className="font-semibold text-slate-700">{loadError.status === 404 ? 'Deal not found' : "Couldn't load this deal"}</div>
          <div className="text-sm text-slate-500 mt-1">{loadError.message}</div>
          <div className="mt-4 flex items-center justify-center gap-3">
            <button type="button" className="btn-outline" onClick={() => { setDeal(null); setAttempt(a => a + 1); }}>Retry</button>
            <Link to="/portal/deals" className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><ArrowLeft size={12} /> All Deals</Link>
          </div>
        </div>
      </div>
    );
  }
  if (!deal) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading deal…</div>;

  const LINK_LABELS = { re: 'Real Estate Status', loan: 'Loan Status', borrower: 'Borrower Portal' };

  const shareUrls = [];
  const txToken = shareOverrides.re || deal.transaction?.share_token;
  const loanToken = shareOverrides.loan || deal.loan?.share_token;
  if (txToken) shareUrls.push({ kind: 're', label: LINK_LABELS.re, url: `${window.location.origin}/tx/${txToken}` });
  if (loanToken) shareUrls.push({ kind: 'loan', label: LINK_LABELS.loan, url: `${window.location.origin}/loan/${loanToken}` });
  if (borrowerLink?.url_path) shareUrls.push({ kind: 'borrower', label: LINK_LABELS.borrower, url: `${window.location.origin}${borrowerLink.url_path}` });

  const amount = fmt$(deal.transaction?.contract_price || deal.loan?.loan_amount);
  const address = addressLine(deal.property?.address);

  const regenerateLink = async (kind) => {
    if (!(await confirmDialog({
      title: `Regenerate the ${LINK_LABELS[kind]} link?`,
      description: 'The current link stops working immediately — anyone who has it will lose access until you share the new one.',
      confirmText: 'Regenerate',
      destructive: true,
    }))) return;
    setRegeneratingKind(kind);
    try {
      if (kind === 'borrower') {
        const doc = await losApi.regenerateBorrowerLink(dealId);
        setBorrowerLink(doc);
      } else if (kind === 'loan') {
        const { share_token } = await losApi.regenerateShareToken(deal.loan.loan_file_id);
        setShareOverrides(s => ({ ...s, loan: share_token }));
      } else {
        const { data } = await api.post(`/re/transactions/${deal.transaction.transaction_id}/regenerate-share-token`);
        setShareOverrides(s => ({ ...s, re: data.share_token }));
      }
      toast({ title: `${LINK_LABELS[kind]} link regenerated` });
    } catch (e) {
      toast({ title: 'Failed to regenerate link', description: errorMessage(e, 'Error') });
    } finally {
      setRegeneratingKind(null);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <Link to="/portal/deals" className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-4"><ArrowLeft size={12} /> All Deals</Link>

      <div className="rounded-2xl p-6 text-white mb-4 shadow" style={{ background: 'linear-gradient(160deg, #030524 0%, #070B5F 60%, #0F1780 100%)' }} data-testid="deal-header">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest" style={{ color: '#A6E56C' }}>
            <Handshake size={12} /> Unified Deal View
          </div>
          <div className="flex items-center gap-2">
            <AudreySummaryButton
              kind="deal"
              id={dealId}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 text-white text-sm hover:bg-white/10 disabled:opacity-60"
            />
            <AskAudreyButton
              kind="deal"
              id={dealId}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 text-white text-sm hover:bg-white/10"
            />
          </div>
        </div>
        {deal.client_name ? (
          <>
            <h1 className="font-serif text-3xl font-bold mt-2" data-testid="deal-client-name">{deal.client_name}</h1>
            <div className="font-serif text-xl font-semibold mt-0.5 text-white/90">{amount}</div>
          </>
        ) : (
          <h1 className="font-serif text-3xl font-bold mt-2">{amount}</h1>
        )}
        {address && <div className="mt-1 text-white/70 text-sm" data-testid="deal-address">{address}</div>}
        <div className="flex gap-3 mt-4 flex-wrap">
          {deal.has_re_side && deal.transaction && <StatChip label="RE Stage" value={stageLabel(deal.transaction.stage)} accent="#A6E56C" />}
          {deal.has_loan_side && deal.loan && <StatChip label="Loan Status" value={stageLabel(deal.loan.status)} accent="#A6E56C" />}
          {deal.loan && <StatChip label="Open Conditions" value={deal.loan.open_conditions} accent="#f59e0b" />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Timeline */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="font-serif font-semibold mb-4" style={{ color: '#070B5F' }}>Unified Timeline</div>
          <div className="relative pl-6 border-l-2 border-slate-100 space-y-4">
            {deal.timeline.map((e, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-[29px] top-1 w-4 h-4 rounded-full border-2 border-white shadow"
                  style={{ background: SOURCE_COLORS[e.source] || '#070B5F' }} />
                <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                  <span className="px-1.5 py-0.5 rounded text-white" style={{ background: SOURCE_COLORS[e.source] || '#070B5F', fontSize: 9 }}>{e.source}</span>
                  {/* RE milestones (contract/effective date) are calendar dates, not times. */}
                  <span data-testid="timeline-time">{e.occurred_at ? (e.category === 're_milestone' ? formatDate(e.occurred_at) : formatDateTime(e.occurred_at)) : ''}</span>
                </div>
                <div className="font-semibold text-sm mt-0.5" style={{ color: '#070B5F' }}>{e.label}</div>
                {e.notes && <div className="text-xs text-slate-500 mt-0.5">{e.notes}</div>}
              </div>
            ))}
            {deal.timeline.length === 0 && <div className="text-sm text-slate-400 italic">No events yet.</div>}
          </div>
        </div>

        <div className="space-y-4">
          {/* Sides */}
          {deal.transaction && (
            <SidePanel title="Real Estate Side" color="#6BA82E">
              <Row label="Stage" value={stageLabel(deal.transaction.stage)} />
              <Row label="Side" value={humanize(deal.transaction.side)} />
              <Row label="Price" value={fmt$(deal.transaction.contract_price)} />
              <Link to={`/portal/real-estate/transactions/${deal.transaction.transaction_id}`} data-testid="deal-open-transaction" className="text-xs text-slate-500 hover:text-slate-700 mt-2 inline-flex items-center gap-1"><ExternalLink size={11} /> Open Transaction</Link>
            </SidePanel>
          )}
          {deal.loan && (
            <SidePanel title="Lending Side" color="#070B5F">
              <Row label="Status" value={stageLabel(deal.loan.status)} />
              <Row label="Program" value={labelFor(PROGRAM_LABELS, deal.loan.loan_program) || '—'} />
              <Row label="Amount" value={fmt$(deal.loan.loan_amount)} />
              <Row label="Rate" value={deal.loan.note_rate ? `${deal.loan.note_rate}%` : '—'} />
              <Row label="Conditions" value={`${deal.loan.open_conditions} open · ${deal.loan.cleared_conditions} cleared`} />
              <Link to={`/portal/loans/${deal.loan.loan_file_id}`} className="text-xs text-slate-500 hover:text-slate-700 mt-2 inline-flex items-center gap-1"><ExternalLink size={11} /> Open Loan File</Link>
              {deal.loan.contact_id && (
                <Link to={`/portal/contacts/${deal.loan.contact_id}?tab=qualification&loan_id=${deal.loan.loan_file_id}`} className="text-xs text-slate-500 hover:text-slate-700 mt-2 inline-flex items-center gap-1"><Calculator size={11} /> Qualification</Link>
              )}
            </SidePanel>
          )}

          {/* Public share links */}
          {shareUrls.length > 0 && (
            <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Public Status Links</div>
              {shareUrls.map((s) => (
                <div key={s.kind} className="flex items-center gap-1 mb-1">
                  <button onClick={async () => { if (await copyText(s.url)) toast({ title: `${s.label} copied` }); else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' }); }}
                    className="flex-1 min-w-0 text-left text-xs bg-slate-50 rounded-lg px-3 py-2 hover:bg-slate-100 flex items-center gap-2 font-mono truncate">
                    <Copy size={10} className="shrink-0" /> <span className="truncate">{s.label}: {s.url}</span>
                  </button>
                  {s.kind === 'borrower' && invite.canInvite && (
                    <button type="button" onClick={invite.send} disabled={invite.sending}
                      data-testid="email-borrower-link-btn" title="Email this link to the borrower"
                      className="shrink-0 p-2 rounded-lg text-slate-400 hover:text-brand-navy hover:bg-slate-100 disabled:opacity-50">
                      {invite.sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                    </button>
                  )}
                  <button type="button" onClick={() => regenerateLink(s.kind)} disabled={regeneratingKind === s.kind}
                    data-testid={`regenerate-${s.kind}-link-btn`} title="Regenerate this link — the current one stops working immediately"
                    className="shrink-0 p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50">
                    <RotateCw size={12} className={regeneratingKind === s.kind ? 'animate-spin' : ''} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Loan documents: a read-only list; the loan file's Documents tab does the rest. */}
      {hasLoanSide && (
        <DocumentsCard loanId={deal.loan.loan_file_id} docs={docs} error={docsError} />
      )}

      {/* Parties + Contracts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="font-serif font-semibold mb-3 flex items-center gap-2" style={{ color: '#070B5F' }}><Users size={14} /> Deal Parties ({deal.parties.length})</div>
          <div className="space-y-1.5">
            {deal.parties.map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50">
                <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: p.source === 'RE' ? '#F3FAE8' : '#EEF2FF', color: p.source === 'RE' ? '#4c7f21' : '#4338ca' }}>{p.source}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: '#070B5F' }}>{p.name}</div>
                  <div className="text-xs text-slate-500">{roleLabel(p.role)}{p.email && ` · ${p.email}`}</div>
                </div>
              </div>
            ))}
            {deal.parties.length === 0 && <div className="text-xs text-slate-400 italic">No parties yet.</div>}
          </div>
        </div>

        {deal.contracts && deal.contracts.length > 0 && (
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="font-serif font-semibold mb-3 flex items-center gap-2" style={{ color: '#070B5F' }}><FileSignature size={14} /> Contracts ({deal.contracts.length})</div>
            <div className="space-y-2">
              {deal.contracts.map(c => (
                <Link key={c.contract_id} to={`/portal/real-estate?tab=contracts&contract=${c.contract_id}`} data-testid={`deal-contract-${c.contract_id}`} className="block p-2 rounded-lg border border-slate-100 hover:bg-slate-50">
                  <div className="text-sm font-semibold" style={{ color: '#070B5F' }}>{c.title}</div>
                  <div className="text-xs text-slate-500">{c.signed_count}/{c.total_signatories} signed · {humanize(c.status)}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Deal Conversation (Phase 3 — LO ↔ Realtor ↔ Borrower thread) */}
      <div className="mt-4">
        <DealMessagesPanel
          apiClient={api}
          dealId={dealId}
          currentUserId={user?.user_id}
          currentUserRole={user?.role}
          canRemoveParticipant={(user?.permissions || []).includes('loans.assign')}
          canAddParticipant={(user?.permissions || []).includes('links.issue')}
          canMuteParticipant={(user?.permissions || []).includes('loans.edit')}
          canRemoveMessage={(user?.permissions || []).includes('staff.manage')}
        />
      </div>
    </div>
  );
}

const SOURCE_COLORS = { RE: '#6BA82E', Loan: '#070B5F', Deal: '#64748b' };

function DocumentsCard({ loanId, docs, error }) {
  const href = `/portal/loans/${loanId}?section=documents`;
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mt-4" data-testid="deal-documents">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-serif font-semibold flex items-center gap-2" style={{ color: '#070B5F' }}>
          <FileText size={14} /> Documents{docs ? ` (${docs.length})` : ''}
        </div>
        <Link to={href} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><ExternalLink size={11} /> Open Documents</Link>
      </div>
      {error ? (
        <div className="text-xs text-slate-500">Documents unavailable: {error}</div>
      ) : docs === null ? (
        <div className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={12} /> Loading documents…</div>
      ) : docs.length === 0 ? (
        <div className="text-xs text-slate-400 italic">No documents on file yet.</div>
      ) : (
        <div className="divide-y divide-slate-50">
          {docs.map((d) => (
            <div key={d.doc_id} className="flex items-center gap-3 py-2" data-testid={`deal-doc-${d.doc_id}`}>
              <div className="flex-1 min-w-0">
                <Link to={href} className="text-sm font-semibold hover:underline truncate block" style={{ color: '#070B5F' }}>{d.name}</Link>
                <div className="text-xs text-slate-500">{docFolderLabel(d.type)}{d.created_at && ` · ${formatDate(d.created_at)}`}</div>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-slate-100 text-slate-600 shrink-0">{docStatusLabel(d.status)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className="font-serif text-lg font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}
function SidePanel({ title, color, children }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
      <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between border-t border-slate-50 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold" style={{ color: '#070B5F' }}>{value}</span>
    </div>
  );
}
